import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  ActionItemDetailDto,
  ActionItemSummaryDto,
  EditActionItemNoteDto,
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  note: string;
  planGuid: string;
};

const useEditActionItemNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemNoteRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemNoteDto = {
      note: variables.note,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/note`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemNoteRequest };
};

const useEditActionItemNote = () => {
  const { editActionItemNoteRequest } = useEditActionItemNoteRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemNoteRequest,
    onMutate: async (variables) => {
      let cachedSummaryData: ActionItemSummaryDto | undefined;
      let cachedDetailData: ActionItemDetailDto | undefined;

      cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        () => ({
          note: variables.note,
        })
      );

      cachedDetailData = await optimisticallyUpdateQueryData<ActionItemDetailDto>(
        queryClient,
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        () => ({
          note: variables.note,
        })
      );

      return { cachedSummaryData, cachedDetailData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid),
        context?.cachedSummaryData
      );

      queryClient.setQueryData(
        queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditActionItemNote };
